// Private color helpers for the Checkbox `accentColor` feature. These are
// intentionally not exported from `index.ts` — they exist only to support the
// wrapper in `Checkbox.tsx`. No color library is used anywhere in this repo, so
// the parsing/luminance/OKLab math is implemented here from scratch.

type Rgb = { r: number; g: number; b: number };

type OkLab = { L: number; a: number; b: number };

/**
 * Parse a color string into 0–255 RGB channels. Handles the two shapes a
 * checkbox accent can take once resolved by `useToken`: a hex string (raw hex
 * accent passed straight through) or an `rgb()`/`rgba()` string. Returns `null`
 * for anything else (named colors, `var(...)`, `hsl(...)`, `color-mix(...)`),
 * which callers treat as "can't compute — use the safe default".
 */
const parseColor = (input: string): Rgb | null => {
  const c = input.trim().toLowerCase();

  if (c.startsWith('#')) {
    let hex = c.slice(1);
    // Expand shorthand (#abc / #abcd) to full form.
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    if (hex.length !== 6 && hex.length !== 8) return null;
    const n = parseInt(hex.slice(0, 6), 16); // drop alpha for luminance/distance
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  const m = c.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };

  return null;
};

/** sRGB transfer function: gamma-encoded channel (0–255) → linear (0–1). */
const linearize = (channel: number): number => {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of an sRGB color. */
const relativeLuminance = ({ r, g, b }: Rgb): number =>
  0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);

/**
 * Returns true when `color` is perceptually light (relative luminance above the
 * WCAG crossover point, `sqrt(1.05 * 0.05) - 0.05 ≈ 0.179`), meaning black
 * gives better contrast than white against it. Falls back to false (treat as
 * dark) so the hover shift moves toward white, matching the behavior when the
 * browser renders `contrast-color()` as white.
 */
export const isLightAccent = (color: string): boolean => {
  const rgb = parseColor(color);
  if (!rgb) return false;
  return relativeLuminance(rgb) > 0.179;
};

/** Convert an sRGB color to OKLab (Björn Ottosson's constants). */
const rgbToOkLab = ({ r, g, b }: Rgb): OkLab => {
  const lr = linearize(r);
  const lg = linearize(g);
  const lb = linearize(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
};

const okLabDistance = (x: OkLab, y: OkLab): number =>
  Math.sqrt((x.L - y.L) ** 2 + (x.a - y.a) ** 2 + (x.b - y.b) ** 2);

const OKLAB_WHITE = rgbToOkLab({ r: 255, g: 255, b: 255 });

/** Fallback shift used when either reference color can't be parsed. */
const FALLBACK_HOVER_SHIFT_PERCENT = 20;

/**
 * Derive the hover shift magnitude (as a percent) from the theme's own
 * `interaction.main` → `interaction.hover` relationship, measured in OKLab so it
 * matches the perceptual space the hover color-mix happens in. Returns the ratio
 * of dist(main, hover) to dist(main, white) — white being the endpoint the
 * default hover moves toward, since it lightens.
 *
 * The scalar magnitude is applied by the caller in whichever direction (toward
 * white or black) fits the accent, independent of the reference's own direction.
 */
export const deriveHoverShiftPercent = (
  main: string,
  hover: string,
): number => {
  const mainRgb = parseColor(main);
  const hoverRgb = parseColor(hover);
  if (!mainRgb || !hoverRgb) return FALLBACK_HOVER_SHIFT_PERCENT;

  const mainLab = rgbToOkLab(mainRgb);
  const spanToWhite = okLabDistance(mainLab, OKLAB_WHITE);
  if (spanToWhite === 0) return FALLBACK_HOVER_SHIFT_PERCENT;

  const fraction = okLabDistance(mainLab, rgbToOkLab(hoverRgb)) / spanToWhite;
  const percent = Math.round(fraction * 100);

  // Clamp to a sane range so a degenerate theme can't produce a no-op or a
  // hover that swings past the endpoint.
  return Math.min(50, Math.max(1, percent));
};
